import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';
import CallToAction from '../CallToAction';

const ForOpsAndAnalystsPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-background antialiased">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            <main className="relative">
                {/* Hero Section */}
                <section className="pt-20 md:pt-32 pb-16 md:pb-24 px-4 relative overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute top-0 left-1/4 w-48 md:w-72 h-48 md:h-72 bg-primary/5 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 right-1/4 w-48 md:w-72 h-48 md:h-72 bg-primary/5 rounded-full blur-3xl" />
                    </div>
                    
                    <div className="container mx-auto max-w-5xl">
                        <div className="text-center space-y-4 md:space-y-6">
                            <div className="inline-flex items-center px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-primary/10 text-primary text-xs md:text-sm font-medium mb-2 md:mb-4">
                                <svg className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                For Ops & Analysts
                            </div>
                            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground">
                                Build Interactive Dashboards<br className="hidden md:block" />
                                <span className="text-primary">No Engineers Required</span>
                            </h1>
                            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                                Turn your Python notebooks and CSVs into shareable, interactive tools with zero backend setup. 
                                Create rich, data-driven experiences that work like documents.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Features Grid */}
                <section className="py-16 md:py-24 bg-muted/50 relative">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="container mx-auto max-w-5xl px-2 sm:px-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
                            <div className="space-y-6 md:space-y-8">
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">Code-Free Interactivity</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            Create rich, clickable experiences using basic Python or CSV files. No complex coding required.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2">Deploy Like a Document</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            Share as easily as a PDF—no servers, no logins, no IT support needed.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2">Unified View</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            Combine charts, tables, filters, and explanations in one living page.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-card rounded-lg border shadow-sm p-4 md:p-6 relative mt-8 md:mt-0">
                                <div className="absolute -top-3 -right-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Live Example
                                </div>
                                <pre className="text-xs md:text-sm text-muted-foreground font-mono overflow-x-auto">
                                    <code>{`# Create an interactive dashboard
import preswald as pw
import pandas as pd

# Load your data
df = pd.read_csv('sales_data.csv')

# Build the dashboard
app = pw.App("Sales Overview")

@app.page("Dashboard")
def dashboard():
    return pw.Column([
        pw.Chart(df, type="line"),
        pw.Table(df.head()),
        pw.Filter("Region", 
                 options=df['region'].unique())
    ])

# Export and share
app.export("sales_dashboard.html")`}</code>
                                </pre>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Use Cases */}
                <section className="py-16 md:py-24">
                    <div className="container mx-auto max-w-5xl px-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">Perfect For</h2>
                        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">KPI Dashboards</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Track key metrics and performance indicators with interactive visualizations.
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Customer Health</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Monitor customer metrics and health scores with interactive summaries.
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Quarterly Reports</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Create dynamic quarterly reports that stakeholders can explore.
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">What-If Analysis</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Build interactive models for scenario planning and analysis.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Benefits */}
                <section className="py-16 md:py-24 bg-muted/50 relative">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="container mx-auto max-w-5xl px-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">Why Analysts Choose Preswald</h2>
                        <div className="grid md:grid-cols-2 gap-6 md:gap-12">
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 relative">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Simplicity
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">No-Code-Like Simplicity</h3>
                                <ul className="space-y-3 md:space-y-4 text-muted-foreground">
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Works with CSVs, SQL, and Markdown</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Drag-and-drop layout; no web dev skills needed</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Instant previews and local testing</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 relative">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Sharing
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Easy Distribution</h3>
                                <ul className="space-y-3 md:space-y-4 text-muted-foreground">
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Export to shareable static files</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Embed in Notion, Confluence, or slide decks</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Works offline and without logins</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                <CallToAction />
            </main>
            <LandingFooter />
        </div>
    );
};

export default ForOpsAndAnalystsPage; 